import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ControlCenter } from "@/features/admin";
import { cn } from "@/lib/utils";
import { percent } from "./format";

const WEEKDAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

/** How many bars of a profile are called out as the peak. */
const TOP_HOURS = 3;
const TOP_DAYS = 1;

const average = (n: number): string =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: n < 10 ? 1 : 0,
  }).format(n);

/** The indexes of the `n` highest non-zero values. */
const topIndexes = (values: number[], n: number): Set<number> =>
  new Set(
    values
      .map((v, i) => [v, i] as const)
      .filter(([v]) => v > 0)
      .sort((a, b) => b[0] - a[0])
      .slice(0, n)
      .map(([, i]) => i),
  );

function Bars({
  values,
  labels,
  tooltips,
  peaks,
  showLabel,
}: {
  values: number[];
  labels: string[];
  tooltips: string[];
  peaks: Set<number>;
  showLabel: (i: number) => boolean;
}) {
  const max = Math.max(1e-9, ...values);
  return (
    <>
      <div className="flex h-[150px] items-end gap-1">
        {values.map((v, i) => (
          <div
            key={i}
            className="flex h-full flex-1 flex-col justify-end"
            title={tooltips[i]}
          >
            <div
              className="w-full rounded-t-sm transition-all duration-700"
              style={{
                height: `${Math.max(2, (v / max) * 100)}%`,
                background: peaks.has(i)
                  ? "var(--chart-1)"
                  : "color-mix(in srgb, var(--chart-1) 35%, transparent)",
                opacity: v ? 1 : 0.25,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-1">
        {labels.map((l, i) => (
          <div
            key={i}
            className={cn(
              "flex-1 text-center text-[10px] text-muted-foreground",
              peaks.has(i) && "font-medium text-foreground",
            )}
          >
            {showLabel(i) ? l : ""}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * When the phones ring: incoming calls by hour of the day and by day of the week, as an
 * average per day over the last four weeks — a habit, so it does not follow the screen's
 * window (an hour, or today, would be a profile of nothing). Hours are the browser's own.
 */
export function PeakHours({ peak }: { peak: ControlCenter["peak_hours"] }) {
  const weeks = peak.days / 7;
  const byHour = Array.from({ length: 24 }, (_, h) =>
    peak.calls.reduce((s, day) => s + (day[h] ?? 0), 0),
  );
  const byDay = peak.calls.map((day) => day.reduce((s, n) => s + n, 0));
  const total = byDay.reduce((s, n) => s + n, 0);

  const hourAvg = byHour.map((n) => n / peak.days);
  const dayAvg = byDay.map((n) => n / weeks);
  const peakHours = topIndexes(byHour, TOP_HOURS);
  const peakDays = topIndexes(byDay, TOP_DAYS);

  const slot = (h: number) => `${h} h – ${(h + 1) % 24} h`;
  const headline = [
    ...[...peakHours]
      .sort((a, b) => byHour[b] - byHour[a])
      .slice(0, 1)
      .map(slot),
    ...[...peakDays].map((d) => WEEKDAYS[d].toLowerCase()),
  ].join(" · ");

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0 gap-4">
        <div>
          <CardTitle className="text-sm">Heures de pointe</CardTitle>
          <p className="text-xs text-muted-foreground">
            Appels entrants, moyenne par jour sur les {peak.days} derniers jours
            ({peak.timezone})
          </p>
        </div>
        {total ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            Pic :{" "}
            <span className="font-medium text-foreground">{headline}</span>
          </span>
        ) : null}
      </CardHeader>
      <CardContent>
        {total ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                Sur 24 heures
              </div>
              <Bars
                values={hourAvg}
                labels={byHour.map((_, h) => `${h}h`)}
                tooltips={byHour.map(
                  (n, h) =>
                    `${slot(h)} : ${average(hourAvg[h])} appel(s) / jour · ${percent(n, total)}`,
                )}
                peaks={peakHours}
                showLabel={(h) => h % 3 === 0 || peakHours.has(h)}
              />
            </div>
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                Sur la semaine
              </div>
              <Bars
                values={dayAvg}
                labels={WEEKDAYS.map((d) => d.slice(0, 3))}
                tooltips={byDay.map(
                  (n, d) =>
                    `${WEEKDAYS[d]} : ${average(dayAvg[d])} appel(s) en moyenne · ${percent(n, total)}`,
                )}
                peaks={peakDays}
                showLabel={() => true}
              />
            </div>
          </div>
        ) : (
          <div className="flex h-[150px] items-center justify-center text-sm text-muted-foreground">
            Pas d'appel entrant sur la période.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
